import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DOC_VERSION } from "@/legal/legalDocs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * B71-QUAT TASK 4: dialog di riconferma accettazione termini al checkout.
 *
 * Mostra 1 o 2 checkbox in base a `/billing/terms-status`:
 * - 1 checkbox (Conferma Condizioni) se `needs_vexatious_again=false`
 * - 2 checkbox (Condizioni+Privacy + Vessatorie) se la doc_version corrente
 *   e cambiata rispetto a quella accettata al signup
 *
 * Props:
 * - open: bool — dialog aperto
 * - onClose: () => void
 * - onConfirm: ({accept_terms_checkout, accept_vexatious, doc_version}) => Promise<void>
 *     chiama il caller con i flag da passare a /billing/checkout
 * - busy: bool — disabilita il bottone durante la chiamata
 */
export default function CheckoutTermsDialog({ open, onClose, onConfirm, busy }) {
  const [status, setStatus] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptVex, setAcceptVex] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAcceptTerms(false);
    setAcceptVex(false);
    setLoadingStatus(true);
    axios.get(`${API}/billing/terms-status`, { withCredentials: true })
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ current_doc_version: DOC_VERSION, last_accepted_version: "", needs_vexatious_again: false }))
      .finally(() => setLoadingStatus(false));
  }, [open]);

  const needsVex = !!status?.needs_vexatious_again;
  const isValid = acceptTerms && (!needsVex || acceptVex);

  const handleConfirm = async () => {
    if (!isValid) return;
    await onConfirm({
      accept_terms_checkout: true,
      accept_vexatious: needsVex ? true : false,
      doc_version: status?.current_doc_version || DOC_VERSION,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="checkout-terms-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1F3C]">Conferma prima del pagamento</DialogTitle>
          <DialogDescription className="text-[#0D1F3C]/60 text-sm">
            Per attivare l'abbonamento dobbiamo registrare la tua accettazione delle condizioni.
          </DialogDescription>
        </DialogHeader>
        {loadingStatus ? (
          <div className="py-6 text-center text-sm text-[#0D1F3C]/50">Caricamento…</div>
        ) : (
          <div className="space-y-4 pt-2">
            <label className="flex items-start gap-2.5 cursor-pointer" htmlFor="co-cb-terms">
              <Checkbox
                id="co-cb-terms"
                checked={acceptTerms}
                onCheckedChange={(v) => setAcceptTerms(Boolean(v))}
                data-testid="checkout-accept-terms"
                className="mt-0.5"
              />
              <span className="text-xs text-[#0D1F3C]/80 leading-relaxed">
                {needsVex ? (
                  <>
                    Dichiaro di aver letto e di accettare le{" "}
                    <Link to="/legal/condizioni-servizio" target="_blank" rel="noopener" className="text-[#1B4FD8] underline">
                      Condizioni Generali del Servizio
                    </Link>{" "}
                    e la{" "}
                    <Link to="/legal/privacy" target="_blank" rel="noopener" className="text-[#1B4FD8] underline">
                      Privacy Policy
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    Confermo l'accettazione delle{" "}
                    <Link to="/legal/condizioni-servizio" target="_blank" rel="noopener" className="text-[#1B4FD8] underline">
                      Condizioni Generali del Servizio
                    </Link>
                    .
                  </>
                )}
              </span>
            </label>
            {needsVex && (
              <label className="flex items-start gap-2.5 cursor-pointer" htmlFor="co-cb-vex">
                <Checkbox
                  id="co-cb-vex"
                  checked={acceptVex}
                  onCheckedChange={(v) => setAcceptVex(Boolean(v))}
                  data-testid="checkout-accept-vexatious"
                  className="mt-0.5"
                />
                <span className="text-xs text-[#0D1F3C]/80 leading-relaxed">
                  Ai sensi degli artt. 1341 e 1342 c.c. approvo specificamente le clausole:
                  {" "}<strong>Art. 3</strong>, <strong>Art. 4</strong>, <strong>Art. 5</strong>,
                  {" "}<strong>Art. 6</strong>, <strong>Art. 7</strong>, <strong>Art. 8</strong>,
                  {" "}<strong>Art. 9</strong> (foro esclusivo di Ascoli Piceno).
                </span>
              </label>
            )}
            {needsVex && (
              <div className="mt-2 p-3 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
                Le Condizioni Generali sono state aggiornate alla versione <strong>{status?.current_doc_version}</strong> (la tua ultima accettazione era <strong>{status?.last_accepted_version || "—"}</strong>): serve una nuova conferma completa.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => onClose && onClose()} disabled={busy} data-testid="checkout-cancel-btn">
                Annulla
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!isValid || busy}
                className="bg-[#1B4FD8] hover:bg-[#0D1F3C] disabled:opacity-50"
                data-testid="checkout-confirm-btn"
              >
                {busy ? "Apertura pagamento…" : "Procedi al pagamento"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
